import React, { useEffect, useState } from "react";
import { useAuth } from "../../../firebase/AuthProvider";
import { fetchTasksWithCache } from "../../../services/taskServices/TaskService";
import TaskList from "../../../interfaces/task/TaskList";
import "./Tasks.css";
import { selectedUserForTask$ } from "../../../services/Subjects/userSubject";
import TaskSection from "./TaskSection";

interface TaskProps {
    userId?: string;
}

const Tasks: React.FC<TaskProps> = ({ userId: userIdFromProp = null }) => {
    const [tasks, setTasks] = useState<TaskList[]>([]);
    const [userFilter, setUserFilter] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

    const { currentUser } = useAuth();

    useEffect(() => {
        const subscription = selectedUserForTask$.subscribe(users => {
            if (users && users.length > 0) {
                setSelectedUserId(users[0].id);
            } else {
                setSelectedUserId(null);
            }
        });
        return () => subscription.unsubscribe();
    }, []);

    const userId = selectedUserId || userIdFromProp;

    useEffect(() => {
        if (!currentUser) return;

        const fetchTasks = async () => {
            const authToken = await currentUser.getIdToken();
            setIsLoading(true);

            let fetchedTasks: TaskList[] = [];

            if (userFilter.length === 0) {
                const targetUserId = userId || currentUser.uid;
                const tasksForSingleUser = await fetchTasksWithCache(authToken, targetUserId);
                fetchedTasks = tasksForSingleUser?.filter((t) => t.status.name !== "COMPLETED") || [];
            } else {
                const allSelectedUserTasks: TaskList[] = [];
                for (const filteredUserId of userFilter) {
                    const tasksForThisUser = await fetchTasksWithCache(
                        authToken,
                        filteredUserId,
                        false
                    );
                    if (tasksForThisUser) {
                        const incompleteTasks = tasksForThisUser.filter(
                            (t) => t.status?.name !== "COMPLETED"
                        );
                        allSelectedUserTasks.push(...incompleteTasks);
                    }
                }
                fetchedTasks = allSelectedUserTasks;
            }

            const sortedTasks = fetchedTasks.sort(
                (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime()
            );
            setTasks(sortedTasks);
            setIsLoading(false);
        };

        fetchTasks();
    }, [currentUser, userFilter, userId]);

    const toggleUserFilter = (ids: string[]) => {
        setUserFilter(ids);
    };

    // Time-based filtering logic. It is assumed this data is used by a child component not shown in the provided code.
    const today = new Date();
    const endOfToday = new Date(today);
    endOfToday.setHours(23, 59, 59, 999);

    const endOfThisWeek = new Date(today);
    endOfThisWeek.setDate(endOfThisWeek.getDate() + (7 - endOfThisWeek.getDay()));

    const endOfNextMonth = new Date(today);
    endOfNextMonth.setMonth(endOfNextMonth.getMonth() + 1);
    endOfNextMonth.setHours(23, 59, 59, 999);

    const agingTasks = tasks.filter(
        (task) =>
            task.tags &&
            task.tags.some((tag: { name: string }) => tag.name === "Initial")
    );

    const tasksDueToday = tasks.filter(
        (task) =>
            new Date(task.endDate) >= today &&
            new Date(task.endDate) <= endOfToday &&
            task.fromTemplate &&
            task.tags &&
            task.tags.every((tag: { name: string }) => tag.name !== "Initial")
    );

    const tasksDueThisWeek = tasks.filter(
        (task) =>
            new Date(task.endDate) > endOfToday &&
            new Date(task.endDate) <= endOfThisWeek &&
            task.fromTemplate &&
            task.tags &&
            task.tags.every((tag: { name: string }) => tag.name !== "Initial")
    );

    const tasksDueThisMonth = tasks.filter(
        (task) =>
            new Date(task.endDate) > endOfThisWeek &&
            new Date(task.endDate) <= endOfNextMonth &&
            task.fromTemplate &&
            task.tags &&
            task.tags.every((tag: { name: string }) => tag.name !== "Initial")
    );

    const overdueTasks = tasks.filter(
        (task) =>
            new Date(task.endDate) < today &&
            task.fromTemplate &&
            task.tags &&
            task.tags.every((tag: { name: string }) => tag.name !== "Initial")
    );

    const nonTemplateInterims = tasks.filter(
        (task) =>
            !task.fromTemplate &&
            task.tags &&
            task.tags.every((tag: { name: string }) => tag.name !== "Initial")
    );

    return (
        <>
            <TaskSection
                userId={userId}
                isLoading={isLoading}
            />
        </>
    );
};

export default Tasks;

